import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Autocomplete, TextField } from '@mui/material';

import TimelineSearchBoxOption from '../../types/timelineSearchBoxOption.type';
import Tag from './components/Tag';

type SearchBoxProps = {
  options: TimelineSearchBoxOption[];
  onApplyFilters: (filters: TimelineSearchBoxOption[]) => void;
};

const SearchBox: FC<SearchBoxProps> = ({ options, onApplyFilters }) => {
  const { t } = useTranslation(['common']);

  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<
    TimelineSearchBoxOption[]
  >([]);
  const [shouldApplyFilters, setShouldApplyFilters] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  useEffect(() => {
    if (shouldApplyFilters) {
      onApplyFilters(selectedOptions);
      setShouldApplyFilters(false);
    }
  }, [shouldApplyFilters, selectedOptions, onApplyFilters]);

  const onUpdateSelectedOption = (newOption: TimelineSearchBoxOption) => {
    setSelectedOptions((prevState) =>
      prevState.map((option) =>
        option.value === newOption.value ? newOption : option,
      ),
    );
  };

  return (
    <div>
      <Autocomplete
        multiple
        value={selectedOptions}
        inputValue={inputValue}
        options={options}
        noOptionsText={t('common:noOptions')}
        isOptionEqualToValue={(option, value) => {
          return option.label === value.label;
        }}
        renderTags={(selectedOptions, getTagProps) => {
          return selectedOptions.map((selectedOption, index) => {
            const { key } = getTagProps({ index });

            return (
              <Tag
                key={key}
                index={index}
                selectedOption={selectedOption}
                getTagProps={getTagProps}
                isFilterPanelOpen={isFilterPanelOpen}
                setShouldApplyFilters={setShouldApplyFilters}
                getNewSelectedOption={onUpdateSelectedOption}
                setIsFilterPanelOpen={setIsFilterPanelOpen}
              />
            );
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name='search'
            placeholder={t('common:search')}
            variant='outlined'
            onKeyDown={(event) => {
              if (isFilterPanelOpen) {
                event.stopPropagation();
              }

              if (event.key === 'Backspace') {
                event.stopPropagation();
              }
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>{t('notes:' + option.label)}</li>
        )}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear') {
            onApplyFilters(options.map((opt) => ({ ...opt, filters: [] })));
          }

          setSelectedOptions(newValue);
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
      />
    </div>
  );
};

export default SearchBox;
